import { Brain, Users, UserCheck, Settings, BarChart3, Calendar, Star, Sparkles, TrendingUp, Gift, FileText, Video, CreditCard, Code, CheckCircle } from 'lucide-react';

export interface WhatsNewFeature {
  id: number;
  title: string;
  description: string;
  date: string;
  type: 'feature' | 'enhancement' | 'improvement';
  category: string;
}

export const whatsNewFeatures: WhatsNewFeature[] = [
  {
    id: 1,
    title: "HivePad Task Management System",
    description: "Comprehensive note-taking and task management system with priority levels, due dates, and family associations for organized workflow management.",
    date: "2025-07-22",
    type: "feature",
    category: "productivity"
  },
  {
    id: 2,
    title: "Bill Assist Payment Intelligence",
    description: "AI-powered billing assistance with smart payment insights, overdue tracking, and priority-based recommendations for optimized revenue collection.",
    date: "2025-07-20",
    type: "feature",
    category: "billing"
  },
  {
    id: 3,
    title: "Hive360 Website Widget System",
    description: "Embeddable widget system allowing organizations to display classes and accept enrollments on their own websites with zero-dependency vanilla JavaScript.",
    date: "2025-07-15",
    type: "feature",
    category: "integration"
  },
  {
    id: 4,
    title: "HiveShare Video Management",
    description: "Comprehensive video platform with Cloudflare Stream integration, upload management, sharing capabilities, and automated storage policies.",
    date: "2025-07-10",
    type: "feature",
    category: "media"
  },
  {
    id: 5,
    title: "Electronic Signature System",
    description: "Complete e-signature solution with electronic form distribution, digital signature collection, and automated document management workflows.",
    date: "2025-07-05",
    type: "feature",
    category: "documents"
  },
  {
    id: 6,
    title: "Form Designer & Builder",
    description: "Visual form builder enabling custom form creation, template management, and reusable form components for streamlined data collection.",
    date: "2025-07-01",
    type: "feature",
    category: "forms"
  },
  {
    id: 7,
    title: "Check-In Kiosk System",
    description: "Digital student check-in system with animated celebrations, real-time attendance tracking, and class time validation for seamless arrivals.",
    date: "2025-06-25",
    type: "feature",
    category: "attendance"
  },
  {
    id: 8,
    title: "Ask Hivetivity AI Assistant",
    description: "Interactive AI chat system with GPT-4 integration for instant answers, intelligent recommendations, and contextual business guidance.",
    date: "2025-06-20",
    type: "feature",
    category: "ai"
  },
  {
    id: 9,
    title: "Smart Query Analysis System",
    description: "AI service using OpenAI embeddings to learn from successful queries and provide intelligent suggestions based on semantic similarity.",
    date: "2025-06-10",
    type: "feature",
    category: "ai"
  },
  {
    id: 10,
    title: "123+ Custom React Hooks Library",
    description: "Extensive collection of 123 custom hooks for data management, form handling, and business logic optimization across the platform.",
    date: "2025-06-05",
    type: "improvement",
    category: "platform"
  },
  {
    id: 11,
    title: "Advanced Schedule Conflict Detection",
    description: "Intelligent scheduling system that automatically detects and prevents booking conflicts, overlaps, and overbooking across all classes.",
    date: "2025-05-30",
    type: "feature",
    category: "calendar"
  },
  {
    id: 12,
    title: "Makeup Class Request System",
    description: "Complete makeup class management with parent request capabilities, tracking, approval workflow, and administrative oversight.",
    date: "2025-05-25",
    type: "feature",
    category: "portal"
  },
  {
    id: 13,
    title: "Material Design Dashboard",
    description: "Complete dashboard redesign with Material Design principles, analytics intelligence cards, and business insight visualization.",
    date: "2025-05-15",
    type: "enhancement",
    category: "platform"
  },
  {
    id: 14,
    title: "Comprehensive Design System",
    description: "Platform-wide design system with Material Design tokens, consistent components, and unified visual language across all interfaces.",
    date: "2025-05-10",
    type: "improvement",
    category: "platform"
  },
  {
    id: 15,
    title: "Parent Portal Enhancement",
    description: "Modern parent portal with enhanced mobile responsiveness, intuitive navigation, and comprehensive family management capabilities.",
    date: "2025-05-05",
    type: "enhancement",
    category: "portal"
  },
  {
    id: 16,
    title: "Student Profile Enhancement",
    description: "Enhanced student management with improved profiles, family relationship display, and comprehensive data organization.",
    date: "2025-04-20",
    type: "enhancement",
    category: "student"
  },
  {
    id: 17,
    title: "Payment Processing Integration",
    description: "Stripe-powered payment processing with invoice management, automated billing, and real-time transaction processing in the parent portal.",
    date: "2025-04-15",
    type: "feature",
    category: "billing"
  },
  {
    id: 18,
    title: "Mobile-First Responsive Design",
    description: "Complete mobile experience optimization with responsive design patterns, touch-friendly interfaces, and cross-platform compatibility.",
    date: "2025-04-10",
    type: "improvement",
    category: "platform"
  },
  {
    id: 19,
    title: "Advanced Search & Filtering",
    description: "Enhanced search functionality with intelligent filtering, real-time results, and comprehensive data discovery across the platform.",
    date: "2025-04-05",
    type: "enhancement",
    category: "platform"
  },
  {
    id: 20,
    title: "Family Member Management",
    description: "Advanced family management with relationship mapping, billing coordination, member invitations, and comprehensive household organization.",
    date: "2025-03-30",
    type: "feature",
    category: "family"
  },
  {
    id: 21,
    title: "Fullscreen Calendar System",
    description: "Edge-to-edge fullscreen calendar with advanced scheduling capabilities and seamless class management interface for comprehensive schedule overview.",
    date: "2025-03-12",
    type: "feature",
    category: "calendar"
  },
  {
    id: 22,
    title: "WCAG Accessibility Compliance",
    description: "Comprehensive accessibility implementation with 306 lines of documentation, WCAG compliance features, and inclusive design patterns.",
    date: "2024-12-15",
    type: "improvement",
    category: "platform"
  }
];

// Utility functions
export const getTypeIcon = (type: string) => {
  switch (type) {
    case 'feature':
      return Star;
    case 'enhancement':
      return Sparkles;
    case 'improvement':
      return TrendingUp;
    default:
      return Gift;
  }
};

export const getTypeColor = (type: string) => {
  switch (type) {
    case 'feature':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'enhancement':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'improvement':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-purple-100 text-purple-800 border-purple-200';
  }
};

export const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'ai':
      return Brain;
    case 'productivity':
      return CheckCircle;
    case 'billing':
      return CreditCard;
    case 'integration':
      return Code;
    case 'media':
      return Video;
    case 'documents':
      return FileText;
    case 'forms':
      return Settings;
    case 'attendance':
      return UserCheck;
    case 'portal':
      return Users;
    case 'staff':
      return UserCheck;
    case 'calendar':
      return Calendar;
    case 'platform':
      return Settings;
    case 'student':
    case 'family':
      return Users;
    default:
      return BarChart3;
  }
};

export const getCategoryColor = (category: string) => {
  switch (category) {
    case 'ai':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'productivity':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'billing':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'integration':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'media':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'documents':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'forms':
      return 'bg-teal-100 text-teal-800 border-teal-200';
    case 'attendance':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'portal':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'staff':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'calendar':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'platform':
      return 'bg-slate-100 text-slate-800 border-slate-200';
    case 'student':
    case 'family':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    default:
      return 'bg-amber-100 text-amber-800 border-amber-200';
  }
};

// Helper function to check if feature is new (within last 30 days)
export const isFeatureNew = (dateString: string) => {
  const featureDate = new Date(dateString);
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
  return featureDate >= thirtyDaysAgo;
};

// Calculate statistics
export const getFeatureStats = () => {
  const totalFeatures = whatsNewFeatures.length;
  
  const featuresByType = whatsNewFeatures.reduce((acc, feature) => {
    acc[feature.type] = (acc[feature.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const featuresByCategory = whatsNewFeatures.reduce((acc, feature) => {
    acc[feature.category] = (acc[feature.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const sortedFeatures = [...whatsNewFeatures].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return {
    totalFeatures,
    featuresByType,
    featuresByCategory,
    sortedFeatures
  };
};